import React, { useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, CircularProgress, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import skillsService from '../services/skills.service';

const modalTextFieldStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563EB',
        },
    },
    '& label.Mui-focused': {
        color: '#2563EB',
    },
};

const AddTestDrawer = ({ open, onClose, onTestCreated }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [testType, setTestType] = useState('technical');
    const [evaluates, setEvaluates] = useState([]);
    const [evaluateInput, setEvaluateInput] = useState('');
    const [timePerQuestion, setTimePerQuestion] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAddCategory = () => {
        if (evaluateInput.trim() && !evaluates.includes(evaluateInput.trim())) {
            setEvaluates([...evaluates, evaluateInput.trim()]);
            setEvaluateInput('');
        }
    };

    const handleRemoveCategory = (category) => {
        setEvaluates(evaluates.filter(c => c !== category));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            setError('Test name is required.');
            return;
        }
        if (testType === 'soft_skills' && evaluates.length === 0) {
            setError('At least one category is required for soft skills tests.');
            return;
        }

        setLoading(true);
        setError('');

        const testData = {
            name: name.trim(),
            description: description.trim() || null,
            test_type: testType,
            evaluates: testType === 'soft_skills' ? evaluates : null,
            time_per_question: timePerQuestion ? parseInt(timePerQuestion, 10) : null,
            is_active: isActive,
            questions: [] // Questions will be added in detail drawer
        };

        try {
            await skillsService.createTest(testData);
            setLoading(false);
            setName('');
            setDescription('');
            setTestType('technical');
            setEvaluates([]);
            setEvaluateInput('');
            setTimePerQuestion('');
            setIsActive(true);
            onTestCreated();
        } catch (err) {
            setLoading(false);
            setError('Failed to create test. Please try again.');
            console.error(err);
        }
    };

    const handleClose = () => {
        setName('');
        setDescription('');
        setTestType('technical');
        setEvaluates([]);
        setEvaluateInput('');
        setTimePerQuestion('');
        setIsActive(true);
        setError('');
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            keepMounted={false}
            disableRestoreFocus={true}
        >
            <Box sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Add New Test</Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <TextField
                            label="Test Name"
                            variant="outlined"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            autoFocus
                            sx={{ ...modalTextFieldStyle, mb: 2 }}
                            error={!!error && !name.trim()}
                        />
                        <TextField
                            label="Description"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            sx={{ ...modalTextFieldStyle, mb: 2 }}
                        />
                        <FormControl fullWidth sx={{ ...modalTextFieldStyle, mb: 2 }}>
                            <InputLabel>Test Type</InputLabel>
                            <Select
                                value={testType}
                                label="Test Type"
                                onChange={(e) => {
                                    setTestType(e.target.value);
                                    if (e.target.value === 'technical') {
                                        setEvaluates([]);
                                    }
                                }}
                            >
                                <MenuItem value="technical">Technical</MenuItem>
                                <MenuItem value="soft_skills">Soft Skills</MenuItem>
                            </Select>
                        </FormControl>
                        {testType === 'soft_skills' && (
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    label="Category"
                                    variant="outlined"
                                    fullWidth
                                    value={evaluateInput}
                                    onChange={(e) => setEvaluateInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCategory();
                                        }
                                    }}
                                    sx={{ ...modalTextFieldStyle, mb: 1 }}
                                    helperText="Press Enter to add category"
                                />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                    {evaluates.map((cat, index) => (
                                        <Chip
                                            key={index}
                                            label={cat}
                                            onDelete={() => handleRemoveCategory(cat)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <TextField
                            label="Time per Question (seconds)"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={timePerQuestion}
                            onChange={(e) => setTimePerQuestion(e.target.value)}
                            sx={{ ...modalTextFieldStyle, mb: 2 }}
                            helperText="Optional: Time limit for each question"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    sx={{
                                        color: '#2563EB',
                                        '&.Mui-checked': {
                                            color: '#2563EB',
                                        },
                                    }}
                                />
                            }
                            label="Active"
                        />
                        {error && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Button onClick={handleClose} sx={{ color: 'grey.700', borderRadius: '12px' }}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                bgcolor: '#2563EB',
                                color: 'white',
                                borderRadius: '12px',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: '#1D4ED8',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AddTestDrawer;











